import React from 'react';
import axios from 'axios';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { Button } from '@lightspeed/flame/Button';
import Group from '@lightspeed/cirrus-group';
import { Spinner } from '@lightspeed/flame/Spinner';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import Text from '@lightspeed/cirrus-text';

import { modalSteps } from '..';
import { toBoolean } from 'pages/utils/sanitize';
import { parseCS4070Barcode, parseDS22Barcode, errorReasons } from '../utils';
import { useEventLog } from './use-event-log';

export const MIN_BARCODE_LENGTH = 8;
const MAX_SCAN_ATTEMPT = 2;
export const SCAN_TIMEOUT_DELAY = 5000;
export const SCAN_DEBOUNCE_DELAY = 150;

const scanAttemptMessages = [
  'Scan customer ID to continue',
  'Make sure you scan the 2D barcode',
  'Try covering the 1D barcode',
];

// The early detection is used to keep the scanning timeout alive if a 2D barcode is being scanned
const Early2DBarcodeRegex = /^@/;
const CS4070HeaderRegex = /^@Controlj6m(ANSI|AAMVA)/;
const DS22MacHeaderRegex = /^@Clear0010ClearClear0030ClearClear0013Clear(ANSI|AAMVA)/;
const DS22WinHeaderRegex = /^@NumLock0010NumLockNumLock0030NumLockNumLock0013NumLock(ANSI|AAMVA)/;
// eslint-disable-next-line no-control-regex
const AAMVAHeaderRegex = /^@\W+(ANSI |AAMVA)[0-9]{10}([0-9]{2})?([A-Z]{2}([0-9]{4})([0-9]{4}))+.*[\W]/;

let isEarly2DBarcodeRegex = false;
let isCS4070Barcode = false;
let isDS22Barcode = false;
let barcode = '';
let bytes = 0;
let scanTimeout = null;

const AgeVerification = ({ setStep, setStepWithData, reject }) => {
  const [isDebouncing, setIsDebouncing] = React.useState();
  const [scanAttemptCount, setScanAttemptCount] = React.useState(0);
  const { submitEvent } = useEventLog();

  const registerBarcodeScan = e => {
    e.preventDefault();
    e.stopPropagation();

    if (['Alt', 'Shift', 'Ctrl', 'Meta', 'Super', 'Hyper', 'Enter'].includes(e.key)) return;
    if (!isDebouncing && barcode.length >= MIN_BARCODE_LENGTH) {
      setIsDebouncing(true);
    }

    barcode += e.key;
  };

  const resetBarcode = () => {
    isEarly2DBarcodeRegex = false;
    isCS4070Barcode = false;
    isDS22Barcode = false;
    barcode = '';
    bytes = 0;
    if (scanTimeout) {
      clearTimeout(scanTimeout);
      scanTimeout = null;
    }
    setIsDebouncing(false);
  };

  React.useEffect(() => {
    window.submit2DBarcodeScan = async parsedBarcode => {
      // If its called directly from stencil in Scanner.js
      if (!isDebouncing) {
        setIsDebouncing(true);
      }

      const rawData = btoa(parsedBarcode);
      let result;

      try {
        result = await axios({
          method: 'post',
          url: `/API/Account/${window.merchantos.account.id}/IdentificationParser.json`,
          data: {
            rawData,
          },
        });
      } catch (e) {
        const { errorClass } = get(e, 'response.data');
        submitEvent(errorClass);
        setScanAttemptCount(count => (count < MAX_SCAN_ATTEMPT ? count + 1 : 0));
      }

      resetBarcode();

      const ageVerificationData = get(result, 'data.AgeVerification');

      if (!ageVerificationData) return;

      const {
        isExpired,
        name,
        documentNumber,
        postalCode,
        dateOfBirth,
        cardExpiry,
        documentType,
      } = ageVerificationData;

      if (
        !name ||
        !dateOfBirth ||
        !cardExpiry ||
        !documentType ||
        (!documentNumber && !postalCode)
      ) {
        // @Note: cycle through the images for now if any of these are missing
        setScanAttemptCount(count => (count < MAX_SCAN_ATTEMPT ? count + 1 : 0));
        return;
      }

      if (toBoolean(isExpired)) {
        setStepWithData({
          step: modalSteps.AGE_VERIFICATION_ERROR,
          data: {
            reason: errorReasons.EXPIRED,
          },
        });
      } else {
        setStepWithData({
          step: modalSteps.AGE_VERIFICATION_CONFIRM,
          data: ageVerificationData,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepareBarcodeScanSubmit = debounce(() => {
    let parsedBarcode = barcode;

    if (!isEarly2DBarcodeRegex) {
      isEarly2DBarcodeRegex = Early2DBarcodeRegex.test(barcode);
    }

    if (!isCS4070Barcode || !isDS22Barcode) {
      if (CS4070HeaderRegex.test(barcode)) {
        isCS4070Barcode = true;
      } else if (DS22MacHeaderRegex.test(barcode) || DS22WinHeaderRegex.test(barcode)) {
        isDS22Barcode = true;
      }
    }

    if (isCS4070Barcode) {
      parsedBarcode = parseCS4070Barcode(barcode);
    } else if (isDS22Barcode) {
      parsedBarcode = parseDS22Barcode(barcode);
    }

    clearTimeout(scanTimeout);
    scanTimeout = isEarly2DBarcodeRegex
      ? setTimeout(() => {
          if (parsedBarcode.length < MIN_BARCODE_LENGTH) {
            resetBarcode();
            return;
          }
          window.submit2DBarcodeScan(parsedBarcode);
        }, SCAN_TIMEOUT_DELAY)
      : null;

    if (isEarly2DBarcodeRegex || isCS4070Barcode || isDS22Barcode) {
      if (!bytes) {
        const [, , , offset, length] = parsedBarcode.match(AAMVAHeaderRegex) || [];

        if (offset && length) {
          bytes = parseFloat(offset) + parseFloat(length);
        } else {
          // If the barcode starts by the AAMVA regex, we NEED a byte offset & length
          return;
        }
      }

      if (bytes && new Blob([parsedBarcode]).size < bytes) {
        // @Note sometime the scanner won't quickly paste the characters, when that happens
        // we wait up to 3 seconds to match the document byte size with the accumulated barcode
        return;
      }
    }

    if (parsedBarcode.length < MIN_BARCODE_LENGTH) {
      resetBarcode();
      return;
    }

    window.submit2DBarcodeScan(parsedBarcode);
  }, SCAN_DEBOUNCE_DELAY);

  React.useEffect(() => {
    document.addEventListener('keydown', registerBarcodeScan, true);
    document.addEventListener('keydown', prepareBarcodeScanSubmit, true);

    return () => {
      window.submit2DBarcodeScan = undefined;
      document.removeEventListener('keydown', registerBarcodeScan, true);
      document.removeEventListener('keydown', prepareBarcodeScanSubmit, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal className="cirrus responsive" isOpen onRequestClose={reject}>
      <ModalHeader onCloseClicked={reject}>Age verification required</ModalHeader>
      <ModalBody className="cr-position-relative">
        <div style={{ padding: '0.375rem' }}>
          <img
            data-test="scanner-img"
            src={`https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/register-rules/scanner${scanAttemptCount}.svg`}
            width="442px"
            height="235px"
            alt="scanner"
          />
        </div>

        <div
          className="cr-justify-center cr-position-absolute cr-text-center"
          style={{ width: 'calc(100% - 3.75rem)', bottom: '1.75rem' }}
        >
          {isDebouncing ? (
            <>
              <Spinner color="blue" className="cr-mr-2" />
              <Text className="cr-display-inline-block">Age verification in progress</Text>
            </>
          ) : (
            <Text>{scanAttemptMessages[scanAttemptCount]}</Text>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Group className="cr-justify-between">
          <a
            className="cr-cursor-pointer"
            href={null}
            onClick={() => setStep(modalSteps.AGE_VERIFICATION_MANUAL)}
          >
            <Text bodySmall color="gray-300" data-test="manager-verification-button">
              Manager verification
            </Text>
          </a>
          <Button onClick={reject}>Cancel</Button>
        </Group>
      </ModalFooter>
    </Modal>
  );
};

export default AgeVerification;
